// app/javascript/controllers/address_controller.js
import { Controller } from "@hotwired/stimulus"
import { Country, State } from "country-state-city"

/**
 * Address Controller for Stimulus JS
 * 
 * Handles address form functionality including:
 * 1. Country dropdown with filtered countries and priority countries
 * 2. Dynamic state/province field based on selected country
 * 3. Google Places address autocomplete
 * 4. Phone country code prefix for international phone numbers
 */
export default class extends Controller {
  static targets = [
    "addressInput", 
    "countrySelect", 
    "stateContainer",
    "stateInput",
    "cityInput",
    "zipcodeInput",
    "phonePrefix",
    "phoneInput"
  ]

  static values = {
    allowedCountries: {
      type: Array,
      default: [
        'US', 'CA', 'GB', 'AU', 'FR', 'DE', 'NZ', 'IE', 
        'ES', 'IT', 'JP', 'NL', 'CH', 'SG', 'SE', 'TR'
      ]
    },
    priorityCountries: {
      type: Array,
      default: ['US', 'CA', 'GB']
    },
    useAutocomplete: { type: Boolean, default: true },
    apiKey: { type: String }
  }

  connect() {
    console.log('Address controller connected');
    
    // Initialize country dropdown
    if (this.hasCountrySelectTarget) {
      this.initializeCountries();
      
      // Add event listener for country changes
      this.countrySelectTarget.addEventListener('change', () => {
        //console.log('Country select change event triggered');
        this.countryChanged();
      });
      
      // Set initial phone prefix based on default country
      if (this.hasPhonePrefixTarget) {
        const countryCode = this.countrySelectTarget.value;
        this.updatePhonePrefix(countryCode);
      }
    }
    
    // Setup address autocomplete if enabled and input exists
    if (this.hasAddressInputTarget && this.useAutocompleteValue) {
      //console.log('Address autocomplete enabled');
      this.loadGoogleMapsApi()
        .then(() => {
          this.setupAddressAutocomplete();
        })
        .catch(error => {
          console.error('Error loading Google Maps API:', error);
        });
    }
  }
  
  /**
   * Load Google Maps API script
   */
  loadGoogleMapsApi() {
    return new Promise((resolve, reject) => {
      // If already loaded, resolve immediately
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        resolve();
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&libraries=places&v=weekly`;
      script.async = true;

      // Setup load and error handlers
      script.onload = () => {
        //console.log('Google Maps API loaded successfully');
        resolve();
      };
      script.onerror = () => {
        //console.error('Failed to load Google Maps API');
        reject(new Error('Failed to load Google Maps API'));
      };

      // Add script to document
      document.head.appendChild(script);
    });
  }
  
  /**
   * Initialize countries dropdown with filtered and priority countries
   */
  initializeCountries() {
    // Get all countries
    const allCountries = Country.getAllCountries();
    
    // Use the values from controller configuration
    const allowedCountryCodes = this.allowedCountriesValue;
    const priorityCountryCodes = this.priorityCountriesValue;
    
    // Filter to only allowed countries
    const filteredCountries = allCountries.filter(country => 
      allowedCountryCodes.includes(country.isoCode)
    );
    
    // Sort all filtered countries alphabetically
    filteredCountries.sort((a, b) => a.name.localeCompare(b.name));
    
    // Split into priority and non-priority countries
    const priorityCountries = [];
    const otherCountries = [];
    
    filteredCountries.forEach(country => {
      if (priorityCountryCodes.includes(country.isoCode)) {
        priorityCountries.push(country);
      } else {
        otherCountries.push(country);
      }
    });
    
    // Store the currently selected value to restore it after rebuilding
    const currentValue = this.countrySelectTarget.value;
    
    // Clear existing options and add a prompt
    this.countrySelectTarget.innerHTML = '<option value="">Select Country</option>';
    
    // Add priority countries first if any
    if (priorityCountries.length > 0) {
      priorityCountries.forEach(country => {
        const option = document.createElement('option');
        option.value = country.isoCode;
        option.textContent = country.name;
        this.countrySelectTarget.appendChild(option);
      });
      
      // Add a separator
      const separatorOption = document.createElement('option');
      separatorOption.disabled = true;
      separatorOption.value = "";
      separatorOption.textContent = "──────────";
      this.countrySelectTarget.appendChild(separatorOption);
    }
    
    // Add the rest of the countries
    otherCountries.forEach(country => {
      const option = document.createElement('option');
      option.value = country.isoCode;
      option.textContent = country.name;
      this.countrySelectTarget.appendChild(option);
    });
    
    // Restore previously selected value if it exists
    if (currentValue) {
      this.countrySelectTarget.value = currentValue;
      this.countryChanged();
    } else if (this.countrySelectTarget.value) {
      // If a value was already set (e.g. from the database)
      this.countryChanged();
    } else if (priorityCountryCodes.length > 0) {
      // Default to first priority country if none selected
      this.countrySelectTarget.value = priorityCountryCodes[0];
      this.countryChanged();
    }
  }
  
  /**
   * Update phone prefix based on country
   */
  updatePhonePrefix(countryCode) {
    if (!this.hasPhonePrefixTarget) return;
    
    const country = Country.getCountryByCode(countryCode);
    if (country) {
      this.phonePrefixTarget.textContent = `+${country.phonecode}`;
    }
  }
  
  /**
   * Handle country dropdown changes
   */
  countryChanged() {
    if (!this.hasStateContainerTarget) return;
    
    const countryCode = this.countrySelectTarget.value;
    console.log('Country changed to:', countryCode);
    
    if (!countryCode) return;
    
    // Update phone prefix
    this.updatePhonePrefix(countryCode);
    
    // Update autocomplete restrictions for the new country
    if (this.autocomplete) {
      this.autocomplete.setComponentRestrictions({
        country: countryCode.toLowerCase()
      });
    }
    
    // Get states for the selected country
    const states = State.getStatesOfCountry(countryCode);
    
    // Get the current state value before we regenerate the field
    // This is important for preserving the selected state when editing
    const currentStateField = this.stateContainerTarget.querySelector('[data-address-target="stateInput"]');
    const currentStateValue = currentStateField ? currentStateField.value : '';
    console.log('Current state value:', currentStateValue);
    
    // Always regenerate the state field when country changes
    if (states.length > 0) {
      // Create a select field for states
      this.stateContainerTarget.innerHTML = `
        <div class="form-floating mb-3">
          <select class="form-select" required name="user[detail_attributes][ship_state]" id="user_detail_attributes_ship_state" data-address-target="stateInput"></select>
          <label for="user_detail_attributes_ship_state">State/Province*</label>
        </div>
      `;
      
      // Get the select element
      const stateSelect = this.stateContainerTarget.querySelector('select');
      
      // Add a default prompt option
      const promptOption = document.createElement('option');
      promptOption.value = '';
      promptOption.textContent = '';
      stateSelect.appendChild(promptOption);
      
      // Add state options
      states.forEach(state => {
        const option = document.createElement('option');
        option.value = state.isoCode;
        option.textContent = state.name;
        stateSelect.appendChild(option);
      });
      
      // Set the previously selected value if it exists in the new options
      if (currentStateValue) {
        // Check if the current state value exists in the available states
        const stateExists = states.some(state => state.isoCode === currentStateValue);
        if (stateExists) {
          stateSelect.value = currentStateValue;
        }
      }
    } else {
      // Create a text input for countries without defined states
      this.stateContainerTarget.innerHTML = `
        <div class="form-floating mb-3">
          <input class="form-control" placeholder="State/Province" required type="text" name="user[detail_attributes][ship_state]" id="user_detail_attributes_ship_state" data-address-target="stateInput" value="${currentStateValue}">
          <label for="user_detail_attributes_ship_state">State/Province*</label>
        </div>
      `;
    }
    
    // Notify any validators about the field change
    this.emitStateFieldChanged();
  }
  
  /**
   * Emit a custom event when the state field changes for validation purposes
   */
  emitStateFieldChanged() {
    const stateField = this.stateContainerTarget.querySelector('[data-address-target="stateInput"]');
    
    const event = new CustomEvent('address:stateFieldChanged', {
      bubbles: true,
      detail: { 
        fieldName: 'user[detail_attributes][ship_state]',
        stateField: stateField
      }
    });
    
    this.element.dispatchEvent(event);
  }
  
  /**
   * Set up Google Places address autocomplete
   */
  setupAddressAutocomplete() {
    // Make sure Google Maps API is loaded
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      console.warn('Google Maps Places API not loaded yet. Address autocomplete will not work until it loads.');
      return;
    }
    
    //console.log('Initializing Google Places autocomplete');
    
    try {
      // Create the autocomplete instance with country restriction
      const countryCode = this.hasCountrySelectTarget ? this.countrySelectTarget.value : null;
      const autocomplete = new google.maps.places.Autocomplete(this.addressInputTarget, {
        types: ['address'],
        fields: ['address_components', 'formatted_address'],
        componentRestrictions: countryCode ? { country: countryCode.toLowerCase() } : {}
      });
      
      // Store the autocomplete instance
      this.autocomplete = autocomplete;
      
      // Listen for place selection
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && place.address_components) {
          this.fillAddressFields(place);
        }
      });
      
      console.log('Google Places autocomplete initialized successfully');
    } catch (error) {
      console.error('Error initializing Google Places autocomplete:', error);
    }
  }
  
  /**
   * Fill form fields with data from Google Places autocomplete
   */
  fillAddressFields(place) {
    if (!place.address_components) {
      console.warn('No address components found in place object');
      return;
    }
    
    // Initialize values
    let streetNumber = '';
    let route = '';
    let city = '';
    let stateCode = '';
    let countryCode = '';
    let postalCode = '';
    
    // Extract data from address components
    place.address_components.forEach(component => {
      const types = component.types || [];
      
      // Use includes instead of just checking the first type to be more reliable
      if (types.includes('street_number')) {
        streetNumber = component.long_name || '';
      } else if (types.includes('route')) {
        route = component.long_name || '';
      } else if (types.includes('locality')) {
        city = component.long_name || '';
      } else if (types.includes('administrative_area_level_1')) {
        stateCode = component.short_name || '';
      } else if (types.includes('postal_code')) {
        postalCode = component.long_name || '';
      }
    });
    
    // Update address field - this is the field where autocomplete was initialized
    if (streetNumber || route) {
      const address = (streetNumber + ' ' + route).trim();
      this.addressInputTarget.value = address;
    }
    
    // Country update removed - we want to preserve the user's country selection
    
    // Update city field
    if (this.hasCityInputTarget && city) {
      this.cityInputTarget.value = city;
    }
    
    // Update postal code field
    if (this.hasZipcodeInputTarget && postalCode) {
      this.zipcodeInputTarget.value = postalCode;
    }
    
    // Update state field - wait for states to load (after country change)
    setTimeout(() => {
      // Find the state field that was created by countryChanged()
      if (this.hasStateInputTarget && stateCode) {
        if (this.stateInputTarget.tagName === 'SELECT') {
          // Try to find the option by value
          const options = this.stateInputTarget.options;
          let found = false;
          
          for (let i = 0; i < options.length; i++) {
            if (options[i].value === stateCode) {
              this.stateInputTarget.selectedIndex = i;
              found = true;
              break;
            }
          }
          
          if (!found) {
            console.warn('Could not find state option for:', stateCode);
          }
        } else {
          // It's a text input
          this.stateInputTarget.value = stateCode;
        }
      }
    }, 500); // Wait for country change to process
  }
}